import React, { useState, useMemo } from 'react';
import {
  Box,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  FormControl,
  Grid,
  InputLabel,
  LinearProgress,
  MenuItem,
  Paper,
  Select,
  SelectChangeEvent,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  IconButton,
  Button,
  Avatar,
  Tabs,
  Tab,
  Divider,
  alpha,
  List,
  ListItem,
  ListItemText,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Tooltip,
} from '@mui/material';
import {
  ArrowBack as ArrowBackIcon,
  Person as PersonIcon,
  Assessment as AssessmentIcon,
  School as SchoolIcon,
  Work as WorkIcon,
  EmojiEvents as TrophyIcon,
  Timeline as TimelineIcon,
  Refresh as RefreshIcon,
  ExpandMore as ExpandMoreIcon,
  Email as EmailIcon,
  Business as BusinessIcon,
  Badge as BadgeIcon,
  CheckCircle as CheckCircleIcon,
  Schedule as ScheduleIcon,
  Cancel as CancelIcon,
  Groups as GroupsIcon,
  Visibility as VisibilityIcon,
  TrackChanges as TrackChangesIcon,
  GridView as GridViewIcon,
} from '@mui/icons-material';
import { useParams, useSearchParams } from 'react-router-dom';
import PageWithHeader from '../common/PageWithHeader';
import { useRouter } from '../../hooks/useRouter';
import { useAppSelector } from '../../redux/hooks';
import { User, useGetUserByIdQuery, useGetEmployeeReviewsByUserByUserIdQuery, useGetLifeWheelsByUserByUserIdQuery, useGet360ReviewsByUserByUserIdQuery, FeedbackSurveyWithRelations, MonthlyIncentiveGoal } from '../../redux/vmsApi';
import { getGoalAchievementStatus, getGoalStatusColor } from '../../utils/goalUtils';
import { useGetPEARByEmployeeQuery, PEARReview } from '../../redux/pearReviewApi';
import { useGetBSCsByUserQuery } from '../../redux/balancedScorecardApi';
import type { BalancedScorecard } from './bscTypes';
import { useGetJobDescriptionByUserIdQuery, useGetMyJobDescriptionQuery } from '../../redux/jobDescriptionApi';
import { useGetOrgChartWithBSCQuery } from '../../redux/balancedScorecardApi';
import { useGetCastingToolsByUserIdQuery, CastingTool } from '../../redux/castingToolApi';
import { PERFORMANCE_RATINGS } from './bscTypes';

// Generate quarters for select dropdown
const generateQuarterOptions = (year: number) => {
  return [
    { value: `Q1 ${year}`, label: `Q1 ${year}` },
    { value: `Q2 ${year}`, label: `Q2 ${year}` },
    { value: `Q3 ${year}`, label: `Q3 ${year}` },
    { value: `Q4 ${year}`, label: `Q4 ${year}` },
  ];
};
import LifeWheelVisualisation from './LifeWheelVisualisation';

// 360° Feedback question mapping
const FEEDBACK_360_QUESTIONS: { [key: string]: string } = {
  respectValues: 'Respect the WestProp Values evident in conduct',
  decisionValues: 'Makes decisions in line with WestProp values',
  encourageValues: 'Encourages team members to live the WestProp values',
  improveValues: 'Suggestions to improve living the WestProp values',
  goodDecisions: 'Makes good decisions and trusts the team when delegating tasks',
  highStandards: 'Team is working to a high standard towards departmental goals',
  motivates: 'Motivates others to reach their goals',
  improveLeadership: 'Suggestions to improve leadership skills',
  controlEmotions: 'Controls emotions even in high pressure situations',
  awareness: 'Demonstrates awareness of how actions affect others',
  strongSkills: 'Has strong skills and interacts well with others',
  empathy: 'Demonstrates empathy and responds appropriately to emotional states',
  selfMotivated: 'Is self motivated, striving to achieve goals',
  improveEmotional: 'Suggestions to improve emotional intelligence',
  urgentWork: 'Delivers urgent work without compromising on details',
  meetsDeadlines: 'Meets deadlines and makes the best time use',
  exceedsExpectations: 'Exceeds expectations despite tight schedules',
  improveProductivity: 'Suggestions to improve productivity',
  fullUnderstanding: 'Has full understanding of role and responsibility',
  expertSkills: 'Is an expert and performs job responsibilities skillfully',
  improveJobKnowledge: 'Suggestions to improve job knowledge',
  gracefulDisagree: 'Disagrees gracefully and respectfully',
  adaptsEasily: 'Adapts easily to various situations and different people',
  equalTreatment: 'Equal treatment of everyone, communicates respectfully',
  improveInterpersonal: 'Suggestions to improve interpersonal skills',
  clearGoalsSelf: 'Sets clear goals, objectives and targets for self',
  clearGoalsTeam: 'Sets clear goals for the team',
  performanceReviews: 'Conducts performance reviews timely and objectively',
  manageUnderperformance: 'Proactively manages underperformance',
  improvePerformance: 'Suggestions to improve performance management',
  strength: 'Greatest strength and what to continue doing',
  opportunity: 'Greatest opportunity and what to improve',
};

const FEEDBACK_360_SECTIONS = [
  { title: 'Values', questions: ['respectValues', 'decisionValues', 'encourageValues', 'improveValues'] },
  { title: 'Leadership Skills', questions: ['goodDecisions', 'highStandards', 'motivates', 'improveLeadership'] },
  { title: 'Emotional Intelligence', questions: ['controlEmotions', 'awareness', 'strongSkills', 'empathy', 'selfMotivated', 'improveEmotional'] },
  { title: 'Productivity', questions: ['urgentWork', 'meetsDeadlines', 'exceedsExpectations', 'improveProductivity'] },
  { title: 'Job Knowledge', questions: ['fullUnderstanding', 'expertSkills', 'improveJobKnowledge'] },
  { title: 'Interpersonal Skills', questions: ['gracefulDisagree', 'adaptsEasily', 'equalTreatment', 'improveInterpersonal'] },
  { title: 'Performance Management', questions: ['clearGoalsSelf', 'clearGoalsTeam', 'performanceReviews', 'manageUnderperformance', 'improvePerformance'] },
];

// Helper to get rating color for 360 feedback (1-10 scale)
const get360RatingColor = (rating: number): string => {
  if (rating >= 8.5) return '#4caf50';
  if (rating >= 6.5) return '#8bc34a';
  if (rating >= 4.5) return '#ff9800';
  return '#f44336';
};

const get360RatingLabel = (rating: number): string => {
  if (rating >= 8.5) return 'Excellent';
  if (rating >= 6.5) return 'Good';
  if (rating >= 4.5) return 'Average';
  if (rating >= 3.5) return 'Below Avg';
  return 'Poor';
};

// Helpers
const getGradeColor = (grade?: string): string => {
  if (!grade) return '#9e9e9e';
  const rating = PERFORMANCE_RATINGS.find(r => r.grade === grade);
  return rating?.colorCode || '#9e9e9e';
};

const getScoreColor = (score?: number): string => {
  if (score === undefined || score === null) return '#9e9e9e';
  if (score >= 110) return '#033e79';
  if (score >= 100) return '#17A2B8';
  if (score >= 85) return '#32CD32';
  if (score >= 70) return '#FFC107';
  return '#DC3545';
};

const getStatusIcon = (status?: string) => {
  switch (status) {
    case 'approved':
      return <CheckCircleIcon color="success" fontSize="small" />;
    case 'submitted':
    case 'under_review':
      return <ScheduleIcon color="warning" fontSize="small" />;
    case 'rejected':
      return <CancelIcon color="error" fontSize="small" />;
    default:
      return undefined;
  }
};

const getStatusColor = (status?: string): 'default' | 'primary' | 'secondary' | 'error' | 'info' | 'success' | 'warning' => {
  switch (status) {
    case 'approved': return 'success';
    case 'submitted': return 'info';
    case 'under_review': return 'warning';
    case 'rejected': return 'error';
    default: return 'default';
  }
};

const getInitials = (name?: string): string => {
  if (!name) return '?';
  return name
    .split(' ')
    .map(n => n[0])
    .join('')
    .toUpperCase()
    .slice(0, 2);
};

// Tab Panel
interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;
  return (
    <div role="tabpanel" hidden={value !== index} {...other}>
      {value === index && <Box sx={{ pt: 3 }}>{children}</Box>}
    </div>
  );
}

// BSC History Card
const BSCHistoryCard: React.FC<{ bsc: BalancedScorecard; onViewDetails?: (bscId: string) => void }> = ({ bsc, onViewDetails }) => (
  <Card variant="outlined" sx={{ mb: 2 }}>
    <CardContent>
      <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
        <Box>
          <Typography variant="subtitle1" fontWeight="bold">
            {bsc.reviewPeriod}
          </Typography>
          <Chip
            icon={getStatusIcon(bsc.status)}
            label={bsc.status}
            size="small"
            color={getStatusColor(bsc.status)}
            variant="outlined"
          />
        </Box>
        <Stack direction="row" spacing={2} alignItems="center">
          <Box sx={{ textAlign: 'right' }}>
            <Typography variant="h4" fontWeight="bold" color={getScoreColor(bsc.totalWeightedScore)}>
              {bsc.totalWeightedScore?.toFixed(1)}%
            </Typography>
            <Chip
              label={bsc.finalGrade}
              size="small"
              sx={{ backgroundColor: getGradeColor(bsc.finalGrade), color: 'white' }}
            />
          </Box>
          {onViewDetails && bsc.id && (
            <Button
              variant="outlined"
              size="small"
              startIcon={<VisibilityIcon />}
              onClick={() => onViewDetails(bsc.id!)}
            >
              View Details
            </Button>
          )}
        </Stack>
      </Stack>

      {/* Perspectives breakdown */}
      <Typography variant="caption" color="text.secondary" gutterBottom>
        Perspective Breakdown
      </Typography>
      <Grid container spacing={1} sx={{ mt: 1 }}>
        {bsc.perspectives?.map((perspective: any) => {
          const score = perspective.perspectiveTotal || 0;
          return (
            <Grid item xs={6} sm={3} key={perspective.name}>
              <Box sx={{ textAlign: 'center', p: 1, borderRadius: 1, backgroundColor: '#f5f5f5' }}>
                <Typography variant="caption" color="text.secondary">
                  {perspective.name}
                </Typography>
                <Typography variant="body2" fontWeight="bold" color={getScoreColor(score * 4)}>
                  {score.toFixed(1)}
                </Typography>
              </Box>
            </Grid>
          );
        })}
      </Grid>

      {bsc.managerComments && (
        <Box sx={{ mt: 2, p: 2, backgroundColor: '#f5f5f5', borderRadius: 1 }}>
          <Typography variant="caption" color="text.secondary">Manager Comments</Typography>
          <Typography variant="body2">{bsc.managerComments}</Typography>
        </Box>
      )}
    </CardContent>
  </Card>
);

// PEAR History Card
const PEARHistoryCard: React.FC<{ pear: PEARReview; onViewDetails?: (pearId: string) => void }> = ({ pear, onViewDetails }) => (
  <Card variant="outlined" sx={{ mb: 2 }}>
    <CardContent>
      <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
        <Box>
          <Typography variant="subtitle1" fontWeight="bold">
            {pear.reviewPeriod}
          </Typography>
          <Chip
            icon={getStatusIcon(pear.status)}
            label={pear.status}
            size="small"
            color={getStatusColor(pear.status)}
            variant="outlined"
          />
        </Box>
        <Stack direction="row" spacing={2} alignItems="center">
          <Box sx={{ textAlign: 'right' }}>
            <Typography variant="h4" fontWeight="bold" color={getScoreColor(pear.overallScore)}>
              {pear.overallScore?.toFixed(1)}%
            </Typography>
            <Chip
              label={pear.finalGrade}
              size="small"
              sx={{ backgroundColor: getGradeColor(pear.finalGrade), color: 'white' }}
            />
          </Box>
          {onViewDetails && pear.id && (
            <Button
              variant="outlined"
              size="small"
              startIcon={<VisibilityIcon />}
              onClick={() => onViewDetails(pear.id)}
            >
              View Details
            </Button>
          )}
        </Stack>
      </Stack>

      {/* Score breakdown */}
      <Grid container spacing={2}>
        <Grid item xs={6}>
          <Box sx={{ p: 2, borderRadius: 1, backgroundColor: '#f5f5f5' }}>
            <Typography variant="caption" color="text.secondary">Functional Skills</Typography>
            <Typography variant="body1" fontWeight="bold">
              {pear.functionalSkillsTotal?.toFixed(0) || 0} / 200
            </Typography>
          </Box>
        </Grid>
        <Grid item xs={6}>
          <Box sx={{ p: 2, borderRadius: 1, backgroundColor: '#f5f5f5' }}>
            <Typography variant="caption" color="text.secondary">Interpersonal Skills</Typography>
            <Typography variant="body1" fontWeight="bold">
              {pear.interpersonalTotal?.toFixed(0) || 0} / 100
            </Typography>
          </Box>
        </Grid>
      </Grid>

      {pear.ratingDescription && (
        <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
          {pear.ratingDescription}
        </Typography>
      )}
    </CardContent>
  </Card>
);

// SPEAR History Card
const SPEARHistoryCard: React.FC<{ spear: any; onViewDetails?: (spearId: string) => void }> = ({ spear, onViewDetails }) => {
  const goal = spear.monthlyIncentiveGoal as MonthlyIncentiveGoal | undefined;
  const goalStatus = getGoalAchievementStatus(goal);
  const goalColor = getGoalStatusColor(goalStatus);
  // Check if manager has already reviewed (has manager data)
  const hasBeenReviewed = Boolean(
    spear.managerEditedAt ||
    spear.managerOverallComments ||
    spear.accomplishments?.some((a: any) => a.managerRating) ||
    spear.quarterlyGoals?.some((g: any) => g.managerRating) ||
    spear.monthlyGoals?.some((g: any) => g.managerRating)
  );
  // Can edit if not approved/rejected
  const canEdit = !spear.status || spear.status === 'draft' || spear.status === 'submitted' || spear.status === 'under_review';
  
  return (
    <Accordion>
      <AccordionSummary expandIcon={<ExpandMoreIcon />}>
        <Stack direction="row" spacing={2} alignItems="center" sx={{ width: '100%', pr: 2 }}>
          <Typography variant="subtitle1" fontWeight="bold">
            {spear.reviewPeriod}
          </Typography>
          <Chip label="Monthly" size="small" variant="outlined" />
          {spear.status && spear.status !== 'draft' && (
            <Chip 
              label={spear.status}
              size="small"
              color={spear.status === 'approved' ? 'success' : spear.status === 'submitted' ? 'info' : spear.status === 'rejected' ? 'error' : 'default'}
              variant="outlined"
            />
          )}
          {goal?.description && (
            <Chip 
              label={`Goal: ${goalStatus === 'achieved' ? '100%+' : goalStatus === 'progress' ? '80-99%' : goalStatus === 'failed' ? '<80%' : 'Pending'}`}
              size="small"
              sx={{ bgcolor: goalColor, color: 'white', ml: 'auto' }}
            />
          )}
          {onViewDetails && spear.id && (
            <Button
              variant={!hasBeenReviewed && canEdit ? "contained" : "outlined"}
              size="small"
              color={!hasBeenReviewed && canEdit ? "primary" : "inherit"}
              startIcon={<VisibilityIcon />}
              onClick={(e) => {
                e.stopPropagation();
                onViewDetails(spear.id);
              }}
              sx={{ ml: 1 }}
            >
              {hasBeenReviewed ? 'View' : 'Review'}
            </Button>
          )}
        </Stack>
      </AccordionSummary>
      <AccordionDetails>
        <Grid container spacing={2}>
          {/* Monthly Incentive Goal Section */}
          {goal?.description && (
            <Grid item xs={12}>
              <Paper sx={{ p: 2, mb: 2, bgcolor: `${goalColor}15`, borderLeft: `4px solid ${goalColor}` }}>
                <Stack direction="row" justifyContent="space-between" alignItems="flex-start">
                  <Box>
                    <Typography variant="subtitle2" fontWeight="bold" color="text.secondary">
                      🎯 Monthly Incentive Goal (50% Bonus)
                    </Typography>
                    <Typography variant="body1" fontWeight="bold" sx={{ mt: 0.5 }}>
                      {goal.description}
                    </Typography>
                    {goal.measure && (
                      <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                        Measure: {goal.measure}
                      </Typography>
                    )}
                    <Stack direction="row" spacing={2} sx={{ mt: 1 }}>
                      <Typography variant="body2">
                        Target: <strong>{goal.targetPercentage || 100}%</strong>
                      </Typography>
                      {goal.actualPercentage !== undefined && (
                        <Typography variant="body2">
                          Actual: <strong>{goal.actualPercentage}%</strong>
                        </Typography>
                      )}
                    </Stack>
                  </Box>
                  <Chip 
                    label={goalStatus === 'achieved' ? 'Achieved' : goalStatus === 'progress' ? 'Progress' : goalStatus === 'failed' ? 'Failed' : 'Pending'}
                    size="medium"
                    sx={{ bgcolor: goalColor, color: 'white', fontWeight: 'bold' }}
                  />
                </Stack>
              </Paper>
            </Grid>
          )}
          <Grid item xs={12} md={4}>
            <Typography variant="caption" color="text.secondary">Accomplishments</Typography>
            <List dense>
              {spear.accomplishments?.map((item: any, idx: number) => (
                <ListItem key={idx}>
                  <ListItemText
                    primary={item.title}
                    secondary={`Rating: ${item.rating || 'N/A'}`}
                  />
                </ListItem>
              ))}
            </List>
          </Grid>
          <Grid item xs={12} md={4}>
            <Typography variant="caption" color="text.secondary">Next Month's Goals</Typography>
            <List dense>
              {spear.monthlyGoals?.map((item: any, idx: number) => (
                <ListItem key={idx}>
                  <ListItemText
                    primary={item.title}
                  />
                </ListItem>
              ))}
            </List>
          </Grid>
          <Grid item xs={12} md={4}>
            <Typography variant="caption" color="text.secondary">Area to Improve</Typography>
            <Typography variant="body2">{spear.areaToImprove}</Typography>
          </Grid>
        </Grid>
      </AccordionDetails>
    </Accordion>
  );
};

// 360 Degree Feedback Summary Card
const Feedback360SummaryCard: React.FC<{ reviews: FeedbackSurveyWithRelations[]; period: string }> = ({ reviews, period }) => {
  // Filter reviews by period
  const periodReviews = reviews.filter(r => r.reviewPeriod === period);
  
  if (periodReviews.length === 0) {
    return (
      <Paper sx={{ p: 4, textAlign: 'center' }}>
        <Typography color="text.secondary">No 360° feedback for {period}</Typography>
      </Paper>
    );
  }

  // Process feedback into summary
  const processFeedback = () => {
    const sectionResults = FEEDBACK_360_SECTIONS.map(section => {
      const ratingQuestions = section.questions.filter(q => !q.includes('improve') && !['strength', 'opportunity'].includes(q));
      const textQuestions = section.questions.filter(q => q.includes('improve') || ['strength', 'opportunity'].includes(q));

      const ratingData = ratingQuestions.map(questionId => {
        const responses = periodReviews.flatMap(review =>
          review.responses.filter(r => r.questionId === questionId)
        );

        const ratings: number[] = [];
        responses.forEach(r => {
          if (r.responseRating !== undefined) {
            ratings.push(r.responseRating);
          } else if (r.responseBoolean !== undefined) {
            ratings.push(r.responseBoolean ? 10 : 1);
          }
        });

        const avgRating = ratings.length > 0
          ? ratings.reduce((a, b) => a + b, 0) / ratings.length
          : 0;

        return {
          questionId,
          question: FEEDBACK_360_QUESTIONS[questionId] || questionId,
          avgRating,
          responseCount: ratings.length,
        };
      });

      const sectionAvg = ratingData.length > 0
        ? ratingData.reduce((sum, item) => sum + item.avgRating, 0) / ratingData.length
        : 0;

      const textResponses = textQuestions.flatMap(questionId => {
        const responses = periodReviews.flatMap(review =>
          review.responses.filter(r => r.questionId === questionId && r.responseText)
        );
        return responses.map(r => ({
          questionId,
          question: FEEDBACK_360_QUESTIONS[questionId] || questionId,
          text: r.responseText!,
        }));
      });

      return { title: section.title, ratingData, textResponses, sectionAvg };
    });

    const overallAvg = sectionResults.length > 0
      ? sectionResults.reduce((sum, s) => sum + s.sectionAvg, 0) / sectionResults.length
      : 0;

    return { totalReviewers: periodReviews.length, overallAvg, sections: sectionResults };
  };

  const summary = processFeedback();

  return (
    <Card variant="outlined" sx={{ mb: 2 }}>
      <CardContent>
        <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 3 }}>
          <Box>
            <Typography variant="h6" fontWeight="bold">
              {period}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {summary.totalReviewers} reviewer(s)
            </Typography>
          </Box>
          <Box sx={{ textAlign: 'right' }}>
            <Typography variant="h4" fontWeight="bold" color={get360RatingColor(summary.overallAvg)}>
              {summary.overallAvg.toFixed(1)}/10
            </Typography>
            <Chip
              label={get360RatingLabel(summary.overallAvg)}
              size="small"
              sx={{ backgroundColor: get360RatingColor(summary.overallAvg), color: 'white' }}
            />
          </Box>
        </Stack>

        {/* Section Averages */}
        <Typography variant="subtitle2" color="text.secondary" gutterBottom>
          Section Breakdown
        </Typography>
        <Grid container spacing={1} sx={{ mb: 3 }}>
          {summary.sections.map(section => (
            <Grid item xs={6} sm={4} md={3} key={section.title}>
              <Box sx={{ p: 1.5, borderRadius: 1, backgroundColor: '#f5f5f5', textAlign: 'center' }}>
                <Typography variant="caption" color="text.secondary">
                  {section.title}
                </Typography>
                <Typography variant="body1" fontWeight="bold" color={get360RatingColor(section.sectionAvg)}>
                  {section.sectionAvg.toFixed(1)}
                </Typography>
              </Box>
            </Grid>
          ))}
        </Grid>

        {/* Detailed Sections as Accordions */}
        {summary.sections.map(section => (
          <Accordion key={section.title} sx={{ mb: 1 }}>
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ width: '100%', pr: 2 }}>
                <Typography variant="subtitle2">{section.title}</Typography>
                <Chip
                  label={`${section.sectionAvg.toFixed(1)}/10`}
                  size="small"
                  sx={{ backgroundColor: get360RatingColor(section.sectionAvg), color: 'white' }}
                />
              </Stack>
            </AccordionSummary>
            <AccordionDetails>
              {/* Rating Questions */}
              {section.ratingData.map(item => (
                <Box key={item.questionId} sx={{ mb: 2 }}>
                  <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 0.5 }}>
                    <Typography variant="body2">{item.question}</Typography>
                    <Typography variant="body2" fontWeight="bold" color={get360RatingColor(item.avgRating)}>
                      {item.avgRating.toFixed(1)}/10
                    </Typography>
                  </Stack>
                  <LinearProgress
                    variant="determinate"
                    value={item.avgRating * 10}
                    sx={{
                      height: 6,
                      borderRadius: 1,
                      backgroundColor: '#e0e0e0',
                      '& .MuiLinearProgress-bar': {
                        backgroundColor: get360RatingColor(item.avgRating),
                      },
                    }}
                  />
                </Box>
              ))}

              {/* Text Responses */}
              {section.textResponses.length > 0 && (
                <Box sx={{ mt: 2 }}>
                  <Typography variant="caption" color="text.secondary" gutterBottom>
                    Feedback Comments
                  </Typography>
                  {section.textResponses.map((response, idx) => (
                    <Box
                      key={idx}
                      sx={{ p: 1.5, mb: 1, backgroundColor: '#f5f5f5', borderRadius: 1, borderLeft: '3px solid #1976d2' }}
                    >
                      <Typography variant="caption" color="text.secondary">
                        {response.question}
                      </Typography>
                      <Typography variant="body2">{response.text}</Typography>
                    </Box>
                  ))}
                </Box>
              )}
            </AccordionDetails>
          </Accordion>
        ))}
      </CardContent>
    </Card>
  );
};

// Main Employee Detail Component
const CEOEmployeeDetail: React.FC = () => {
  const router = useRouter();
  const { employeeId } = useParams<{ employeeId: string }>();
  const [searchParams] = useSearchParams();
  const periodFromUrl = searchParams.get('period');
  const currentUser = useAppSelector(state => state.auth.user);
  const currentYear = new Date().getFullYear();

  // Check if user has company-wide view (CEO, COO, admin)
  const hasCompanyWideView = currentUser?.roles?.includes('admin') || 
    currentUser?.roles?.includes('ceo') || 
    currentUser?.roles?.includes('coo');

  const availableQuarters = useMemo(() => [
    ...generateQuarterOptions(currentYear),
    ...generateQuarterOptions(currentYear - 1),
  ], [currentYear]);
  const [selectedPeriod, setSelectedPeriod] = useState<string>(periodFromUrl || availableQuarters[0]?.value || '');
  const [activeTab, setActiveTab] = useState(0);

  // Fetch employee data
  const { data: employee, isLoading: loadingEmployee } = useGetUserByIdQuery(
    { id: employeeId || '' },
    { skip: !employeeId }
  );
  const { data: jobDescription, isLoading: loadingJD } = useGetJobDescriptionByUserIdQuery(
    { userId: employeeId || '' },
    { skip: !employeeId }
  );
  const { data: myJobDescription, isLoading: loadingMyJD } = useGetMyJobDescriptionQuery();
  const { data: orgChartData, isLoading: loadingOrgChart } = useGetOrgChartWithBSCQuery(
    { period: selectedPeriod },
    { skip: hasCompanyWideView } // Only fetch if we need to check access
  );
  const { data: bscReviews, isLoading: loadingBSC, refetch: refetchBSC } = useGetBSCsByUserQuery(
    { userId: employeeId || '' },
    { skip: !employeeId }
  );
  const { data: pearReviews, isLoading: loadingPEAR, refetch: refetchPEAR } = useGetPEARByEmployeeQuery(
    { employeeId: employeeId || '' },
    { skip: !employeeId }
  );
  const { data: spearReviews, isLoading: loadingSPEAR } = useGetEmployeeReviewsByUserByUserIdQuery(
    { userId: employeeId || '' },
    { skip: !employeeId }
  );
  const { data: lifeWheels, isLoading: loadingLifeWheel } = useGetLifeWheelsByUserByUserIdQuery(
    { userId: employeeId || '' },
    { skip: !employeeId }
  );
  const { data: feedback360Reviews, isLoading: loading360, refetch: refetch360 } = useGet360ReviewsByUserByUserIdQuery(
    { userId: employeeId || '' },
    { skip: !employeeId }
  );
  const { data: castingTools, isLoading: loadingCastingTool } = useGetCastingToolsByUserIdQuery(
    { userId: employeeId || '' },
    { skip: !employeeId }
  );

  const isLoading = loadingEmployee || loadingJD || loadingMyJD || loadingOrgChart || loadingBSC || loadingPEAR || loadingSPEAR || loadingLifeWheel || loading360 || loadingCastingTool;

  // Get current user's department and job description ID
  const myDepartment = myJobDescription?.department;
  const myJobDescriptionId = myJobDescription?.id;
  const employeeDepartment = jobDescription?.department;

  // Helper function to recursively find all subordinates
  const getAllSubordinateIds = useMemo(() => {
    if (!orgChartData || !myJobDescriptionId || hasCompanyWideView) return new Set<string>();
    
    const subordinateIds = new Set<string>();
    
    // Create a map of job description ID to their direct reports
    const reportsToMap = new Map<string, string[]>();
    orgChartData.forEach(node => {
      const reportsTo = node.jobDescription.reportsToJobDescriptionId;
      if (reportsTo) {
        if (!reportsToMap.has(reportsTo)) {
          reportsToMap.set(reportsTo, []);
        }
        reportsToMap.get(reportsTo)!.push(node.jobDescription.id);
      }
    });
    
    // Create a map of job description ID to employee ID
    const jdToEmployeeMap = new Map<string, string>();
    orgChartData.forEach(node => {
      if (node.employee?.id) {
        jdToEmployeeMap.set(node.jobDescription.id, node.employee.id);
      }
    });
    
    // Recursively find all subordinates
    const findSubordinates = (jdId: string) => {
      const directReports = reportsToMap.get(jdId) || [];
      directReports.forEach(reportJdId => {
        const empId = jdToEmployeeMap.get(reportJdId);
        if (empId) {
          subordinateIds.add(empId);
        }
        findSubordinates(reportJdId);
      });
    };
    
    findSubordinates(myJobDescriptionId);
    return subordinateIds;
  }, [orgChartData, myJobDescriptionId, hasCompanyWideView]);

  // Check if user can access this employee
  const canAccessEmployee = useMemo(() => {
    if (hasCompanyWideView) return true;
    
    // User can access if employee is in the same department
    if (myDepartment && employeeDepartment && myDepartment === employeeDepartment) return true;
    
    // Or if employee is a subordinate
    if (employeeId && getAllSubordinateIds.has(employeeId)) return true;
    
    return false;
  }, [hasCompanyWideView, myDepartment, employeeDepartment, employeeId, getAllSubordinateIds]);

  // Filter out drafts from all reviews
  const filteredBSCReviews = useMemo(() => 
    bscReviews?.filter(bsc => bsc.status !== 'draft') || [],
    [bscReviews]
  );
  const filteredPEARReviews = useMemo(() => 
    pearReviews?.filter(pear => pear.status !== 'draft') || [],
    [pearReviews]
  );
  const filtered360Reviews = useMemo(() => 
    feedback360Reviews?.filter(r => r.status === 'submitted') || [],
    [feedback360Reviews]
  );

  // Get latest reviews (from filtered, non-draft reviews)
  const latestBSC = filteredBSCReviews.find(bsc => bsc.reviewPeriod === selectedPeriod) || filteredBSCReviews[0];
  const latestPEAR = filteredPEARReviews.find(pear => pear.reviewPeriod === selectedPeriod) || filteredPEARReviews[0];
  const latestLifeWheel = lifeWheels?.find(lw => lw.reviewPeriod?.includes(selectedPeriod.split(' ')[1])) || lifeWheels?.[0];

  // Handlers
  const handlePeriodChange = (event: SelectChangeEvent) => {
    setSelectedPeriod(event.target.value);
  };

  const handleRefresh = () => {
    refetchBSC();
    refetchPEAR();
    refetch360();
  };

  const handleTabChange = (_: React.SyntheticEvent, newValue: number) => {
    setActiveTab(newValue);
  };

  const handleViewBSCDetails = (bscId: string) => {
    router.push(`/reviews/bsc/review/${bscId}`);
  };

  const handleViewPEARDetails = (pearId: string) => {
    router.push(`/reviews/pear/review/${pearId}`);
  };

  // Check access using canAccessEmployee (computed above)
  if (!canAccessEmployee && !isLoading) {
    return (
      <PageWithHeader>
        <Box sx={{ p: 4, textAlign: 'center' }}>
          <Typography variant="h5" color="error">Access Denied</Typography>
          <Typography color="text.secondary" sx={{ mt: 1 }}>
            You do not have permission to view this employee's details.
          </Typography>
          <Button
            startIcon={<ArrowBackIcon />}
            onClick={() => router.push('/reviews/ceo-dashboard')}
            sx={{ mt: 2 }}
          >
            Back to Dashboard
          </Button>
        </Box>
      </PageWithHeader>
    );
  }

  return (
    <PageWithHeader>
      <Box sx={{ maxWidth: 1400, mx: 'auto', p: 3 }}>
        {/* Header */}
        <Stack direction="row" justifyContent="space-between" alignItems="flex-start" sx={{ mb: 3 }}>
          <Box>
            <Button
              startIcon={<ArrowBackIcon />}
              onClick={() => router.push('/reviews/ceo-dashboard')}
              sx={{ mb: 1 }}
            >
              Back
            </Button>
            <Typography variant="h4" fontWeight="bold" color="primary">
              Employee Performance
            </Typography>
          </Box>
          <Stack direction="row" spacing={2} alignItems="center">
            <FormControl size="small" sx={{ minWidth: 150 }}>
              <InputLabel>Period</InputLabel>
              <Select
                value={selectedPeriod}
                label="Period"
                onChange={handlePeriodChange}
              >
                {availableQuarters.map((q) => (
                  <MenuItem key={q.value} value={q.value}>{q.label}</MenuItem>
                ))}
              </Select>
            </FormControl>
            <IconButton onClick={handleRefresh} color="primary">
              <RefreshIcon />
            </IconButton>
          </Stack>
        </Stack>

        {isLoading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
            <CircularProgress />
          </Box>
        ) : employee ? (
          <>
            {/* Employee Profile Card */}
            <Paper sx={{ p: 3, mb: 4 }}>
              <Grid container spacing={3}>
                <Grid item xs={12} md={3}>
                  <Stack alignItems="center" spacing={2}>
                    <Avatar
                      sx={{
                        width: 120,
                        height: 120,
                        bgcolor: '#1976d2',
                        fontSize: '2.5rem',
                      }}
                    >
                      {getInitials(employee.name)}
                    </Avatar>
                    <Box sx={{ textAlign: 'center' }}>
                      <Typography variant="h5" fontWeight="bold">
                        {employee.name}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        {jobDescription?.title || 'No Job Description'}
                      </Typography>
                    </Box>
                  </Stack>
                </Grid>

                <Grid item xs={12} md={5}>
                  <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                    Employee Details
                  </Typography>
                  <Stack spacing={1.5}>
                    <Stack direction="row" alignItems="center" spacing={1}>
                      <EmailIcon fontSize="small" color="action" />
                      <Typography variant="body2">{employee.email}</Typography>
                    </Stack>
                    <Stack direction="row" alignItems="center" spacing={1}>
                      <BusinessIcon fontSize="small" color="action" />
                      <Typography variant="body2">{jobDescription?.department || 'N/A'}</Typography>
                    </Stack>
                    <Stack direction="row" alignItems="center" spacing={1}>
                      <BadgeIcon fontSize="small" color="action" />
                      <Typography variant="body2">Grade: {jobDescription?.grade || 'N/A'}</Typography>
                    </Stack>
                    <Stack direction="row" alignItems="center" spacing={1}>
                      <PersonIcon fontSize="small" color="action" />
                      <Typography variant="body2">Reports to: {jobDescription?.reportsTo || 'N/A'}</Typography>
                    </Stack>
                  </Stack>
                </Grid>

                <Grid item xs={12} md={4}>
                  <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                    Latest Performance
                  </Typography>
                  <Grid container spacing={2}>
                    <Grid item xs={6}>
                      <Box sx={{
                        p: 2,
                        borderRadius: 2,
                        backgroundColor: alpha(getScoreColor(latestBSC?.totalWeightedScore), 0.1),
                        textAlign: 'center',
                      }}>
                        <Typography variant="caption" color="text.secondary">BSC</Typography>
                        <Typography variant="h5" fontWeight="bold" color={getScoreColor(latestBSC?.totalWeightedScore)}>
                          {latestBSC?.totalWeightedScore?.toFixed(1) || 'N/A'}%
                        </Typography>
                        {latestBSC?.finalGrade && (
                          <Chip
                            label={latestBSC.finalGrade}
                            size="small"
                            sx={{ backgroundColor: getGradeColor(latestBSC.finalGrade), color: 'white', mt: 0.5 }}
                          />
                        )}
                      </Box>
                    </Grid>
                    <Grid item xs={6}>
                      <Box sx={{
                        p: 2,
                        borderRadius: 2,
                        backgroundColor: alpha(getScoreColor(latestPEAR?.overallScore), 0.1),
                        textAlign: 'center',
                      }}>
                        <Typography variant="caption" color="text.secondary">PEAR</Typography>
                        <Typography variant="h5" fontWeight="bold" color={getScoreColor(latestPEAR?.overallScore)}>
                          {latestPEAR?.overallScore?.toFixed(1) || 'N/A'}%
                        </Typography>
                        {latestPEAR?.finalGrade && (
                          <Chip
                            label={latestPEAR.finalGrade}
                            size="small"
                            sx={{ backgroundColor: getGradeColor(latestPEAR.finalGrade), color: 'white', mt: 0.5 }}
                          />
                        )}
                      </Box>
                    </Grid>
                  </Grid>
                </Grid>
              </Grid>
            </Paper>

            {/* Tabs */}
            <Paper sx={{ mb: 4 }}>
              <Tabs value={activeTab} onChange={handleTabChange} variant="fullWidth">
                <Tab icon={<AssessmentIcon />} label="BSC Reviews" />
                <Tab icon={<WorkIcon />} label="PEAR Reviews" />
                <Tab icon={<GroupsIcon />} label="360° Feedback" />
                <Tab icon={<TrackChangesIcon />} label="Incentive Goals" />
                <Tab icon={<TimelineIcon />} label="SPEAR (Monthly)" />
                <Tab icon={<SchoolIcon />} label="Life Wheel" />
                <Tab icon={<GridViewIcon />} label="Casting Tool" />
              </Tabs>
            </Paper>

            {/* Tab Panels */}
            <TabPanel value={activeTab} index={0}>
              <Typography variant="h6" fontWeight="bold" gutterBottom>
                Balanced Scorecard History
              </Typography>
              {filteredBSCReviews.length > 0 ? (
                filteredBSCReviews.map((bsc) => (
                  <BSCHistoryCard key={bsc.id} bsc={bsc} onViewDetails={handleViewBSCDetails} />
                ))
              ) : (
                <Paper sx={{ p: 4, textAlign: 'center' }}>
                  <Typography color="text.secondary">No BSC reviews found</Typography>
                </Paper>
              )}
            </TabPanel>

            <TabPanel value={activeTab} index={1}>
              <Typography variant="h6" fontWeight="bold" gutterBottom>
                PEAR Review History
              </Typography>
              {filteredPEARReviews.length > 0 ? (
                filteredPEARReviews.map((pear) => (
                  <PEARHistoryCard key={pear.id} pear={pear} onViewDetails={handleViewPEARDetails} />
                ))
              ) : (
                <Paper sx={{ p: 4, textAlign: 'center' }}>
                  <Typography color="text.secondary">No PEAR reviews found</Typography>
                </Paper>
              )}
            </TabPanel>

            <TabPanel value={activeTab} index={2}>
              <Typography variant="h6" fontWeight="bold" gutterBottom>
                360° Feedback Reviews
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Aggregated feedback from peers, subordinates, and supervisors
              </Typography>
              {filtered360Reviews.length > 0 ? (
                <Feedback360SummaryCard reviews={filtered360Reviews} period={selectedPeriod} />
              ) : (
                <Paper sx={{ p: 4, textAlign: 'center' }}>
                  <Typography color="text.secondary">No 360° feedback reviews found</Typography>
                </Paper>
              )}
            </TabPanel>

            <TabPanel value={activeTab} index={3}>
              <Typography variant="h6" fontWeight="bold" gutterBottom>
                🎯 Monthly Incentive Goals (50% Bonus)
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Track employee's monthly performance goals and achievement status
              </Typography>
              
              {spearReviews && spearReviews.length > 0 ? (
                <Paper sx={{ p: 2 }}>
                  <TableContainer>
                    <Table size="small">
                      <TableHead>
                        <TableRow sx={{ bgcolor: '#f5f5f5' }}>
                          <TableCell sx={{ fontWeight: 'bold' }}>Month</TableCell>
                          <TableCell sx={{ fontWeight: 'bold' }}>Goal</TableCell>
                          <TableCell sx={{ fontWeight: 'bold' }}>Measure</TableCell>
                          <TableCell align="center" sx={{ fontWeight: 'bold' }}>Target</TableCell>
                          <TableCell align="center" sx={{ fontWeight: 'bold' }}>Actual</TableCell>
                          <TableCell align="center" sx={{ fontWeight: 'bold' }}>Status</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {spearReviews.map((spear: any) => {
                          const goal = spear.monthlyIncentiveGoal as MonthlyIncentiveGoal | undefined;
                          const status = getGoalAchievementStatus(goal);
                          const statusColor = getGoalStatusColor(status);
                          // Extract full month name from reviewPeriod (e.g., "October 2025" -> "October")
                          const monthParts = spear.reviewPeriod?.split(' ') || [];
                          const monthName = monthParts[0] || 'Unknown';
                          const year = monthParts[1] || '';
                          return (
                            <TableRow key={spear.id} sx={{ '&:hover': { bgcolor: '#f9f9f9' } }}>
                              <TableCell>
                                <Typography variant="body2" fontWeight="bold">
                                  {monthName}
                                </Typography>
                                <Typography variant="caption" color="text.secondary">
                                  {year}
                                </Typography>
                              </TableCell>
                              <TableCell>
                                <Typography variant="body2">
                                  {goal?.description || <span style={{ color: '#9e9e9e', fontStyle: 'italic' }}>Not set</span>}
                                </Typography>
                              </TableCell>
                              <TableCell>
                                <Typography variant="body2" color="text.secondary">
                                  {goal?.measure || '-'}
                                </Typography>
                              </TableCell>
                              <TableCell align="center">
                                {goal?.targetPercentage !== undefined ? `${goal.targetPercentage}%` : '-'}
                              </TableCell>
                              <TableCell align="center">
                                {goal?.actualPercentage !== undefined ? (
                                  <Typography fontWeight="bold" color={statusColor}>{goal.actualPercentage}%</Typography>
                                ) : '-'}
                              </TableCell>
                              <TableCell align="center">
                                {goal?.description ? (
                                  <Chip
                                    size="small"
                                    label={status === 'achieved' ? 'Achieved' : status === 'progress' ? 'Progress' : status === 'failed' ? 'Failed' : 'Pending'}
                                    sx={{ bgcolor: statusColor, color: 'white', fontWeight: 'bold' }}
                                  />
                                ) : (
                                  <Typography variant="body2" color="text.secondary">-</Typography>
                                )}
                              </TableCell>
                            </TableRow>
                          );
                        })}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </Paper>
              ) : (
                <Paper sx={{ p: 4, textAlign: 'center' }}>
                  <Typography color="text.secondary">No incentive goals found for this period</Typography>
                </Paper>
              )}
            </TabPanel>

            <TabPanel value={activeTab} index={4}>
              <Typography variant="h6" fontWeight="bold" gutterBottom>
                SPEAR Monthly Reviews
              </Typography>
              
              {spearReviews && spearReviews.length > 0 ? (
                <Stack spacing={1}>
                  {spearReviews.map((spear: any) => (
                    <SPEARHistoryCard key={spear.id} spear={spear} onViewDetails={(spearId) => router.push(`/reviews/spear/review/${spearId}`)} />
                  ))}
                </Stack>
              ) : (
                <Paper sx={{ p: 4, textAlign: 'center' }}>
                  <Typography color="text.secondary">No SPEAR reviews found</Typography>
                </Paper>
              )}
            </TabPanel>

            <TabPanel value={activeTab} index={5}>
              <Typography variant="h6" fontWeight="bold" gutterBottom>
                Life Wheel Assessments
              </Typography>
              {latestLifeWheel && latestLifeWheel.responses?.[0] ? (
                <Grid container spacing={3}>
                  <Grid item xs={12} md={8}>
                    <Paper sx={{ p: 3 }}>
                      <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
                        {latestLifeWheel.reviewPeriod}
                      </Typography>
                      <LifeWheelVisualisation data={latestLifeWheel.responses[0]} width={500} />
                    </Paper>
                  </Grid>
                  <Grid item xs={12} md={4}>
                    <Paper sx={{ p: 3 }}>
                      <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                        Category Scores
                      </Typography>
                      <Stack spacing={2}>
                        {['UPWARDS', 'ONWARDS', 'OUTWARDS'].map((category, catIdx) => {
                          const startIdx = catIdx === 0 ? 0 : catIdx === 1 ? 4 : 9;
                          const endIdx = catIdx === 0 ? 4 : catIdx === 1 ? 9 : 12;
                          const scores = latestLifeWheel.responses[0].current.slice(startIdx, endIdx);
                          const avgScore = scores.reduce((a: number, b: number) => a + b, 0) / scores.length;

                          return (
                            <Box key={category}>
                              <Stack direction="row" justifyContent="space-between">
                                <Typography variant="body2">{category}</Typography>
                                <Typography variant="body2" fontWeight="bold">
                                  {avgScore.toFixed(1)}/10
                                </Typography>
                              </Stack>
                              <LinearProgress
                                variant="determinate"
                                value={avgScore * 10}
                                sx={{
                                  height: 8,
                                  borderRadius: 1,
                                  backgroundColor: '#e0e0e0',
                                  '& .MuiLinearProgress-bar': {
                                    backgroundColor: avgScore >= 7 ? '#4caf50' : avgScore >= 4 ? '#ff9800' : '#f44336',
                                  },
                                }}
                              />
                            </Box>
                          );
                        })}
                      </Stack>

                      <Divider sx={{ my: 2 }} />

                      <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                        Previous Submissions
                      </Typography>
                      <Stack spacing={1}>
                        {lifeWheels?.slice(0, 5).map((lw: any) => (
                          <Chip
                            key={lw.id}
                            label={lw.reviewPeriod}
                            size="small"
                            variant={lw.id === latestLifeWheel.id ? 'filled' : 'outlined'}
                            color={lw.id === latestLifeWheel.id ? 'primary' : 'default'}
                          />
                        ))}
                      </Stack>
                    </Paper>
                  </Grid>
                </Grid>
              ) : (
                <Paper sx={{ p: 4, textAlign: 'center' }}>
                  <Typography color="text.secondary">No Life Wheel assessments found</Typography>
                </Paper>
              )}
            </TabPanel>

            {/* Tab 6: Casting Tool */}
            <TabPanel value={activeTab} index={6}>
              <Typography variant="h6" fontWeight="bold" gutterBottom>
                Casting Tool (4x4) Submissions
              </Typography>
              {castingTools && castingTools.length > 0 ? (
                <Grid container spacing={3}>
                  {castingTools.map((ct) => (
                    <Grid item xs={12} md={6} key={ct.id}>
                      <Paper sx={{ p: 3 }}>
                        <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
                          <Typography variant="h6" color="primary">
                            {ct.reviewPeriod}
                          </Typography>
                          <Chip
                            icon={<CheckCircleIcon />}
                            label="Submitted"
                            color="success"
                            size="small"
                          />
                        </Stack>
                        <Divider sx={{ mb: 2 }} />
                        
                        {/* Section 1: Best Performance - Better */}
                        <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>
                          1. Best Performance: Better
                        </Typography>
                        <Grid container spacing={1} sx={{ mb: 2 }}>
                          <Grid item xs={6}>
                            <Typography variant="caption" color="text.secondary">Alert</Typography>
                            <Typography variant="body2" sx={{ minHeight: 40 }}>{ct.entries.alert || '-'}</Typography>
                          </Grid>
                          <Grid item xs={6}>
                            <Typography variant="caption" color="text.secondary">Curious</Typography>
                            <Typography variant="body2" sx={{ minHeight: 40 }}>{ct.entries.curious || '-'}</Typography>
                          </Grid>
                          <Grid item xs={6}>
                            <Typography variant="caption" color="text.secondary">Responsive</Typography>
                            <Typography variant="body2" sx={{ minHeight: 40 }}>{ct.entries.responsive || '-'}</Typography>
                          </Grid>
                          <Grid item xs={6}>
                            <Typography variant="caption" color="text.secondary">Resourceful</Typography>
                            <Typography variant="body2" sx={{ minHeight: 40 }}>{ct.entries.resourceful || '-'}</Typography>
                          </Grid>
                        </Grid>
                        
                        {/* Section 2: Best Results - Bigger */}
                        <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>
                          2. Best Results: Bigger
                        </Typography>
                        <Grid container spacing={1} sx={{ mb: 2 }}>
                          <Grid item xs={6}>
                            <Typography variant="caption" color="text.secondary">Faster</Typography>
                            <Typography variant="body2" sx={{ minHeight: 40 }}>{ct.entries.faster || '-'}</Typography>
                          </Grid>
                          <Grid item xs={6}>
                            <Typography variant="caption" color="text.secondary">Easier</Typography>
                            <Typography variant="body2" sx={{ minHeight: 40 }}>{ct.entries.easier || '-'}</Typography>
                          </Grid>
                          <Grid item xs={6}>
                            <Typography variant="caption" color="text.secondary">Bigger</Typography>
                            <Typography variant="body2" sx={{ minHeight: 40 }}>{ct.entries.bigger || '-'}</Typography>
                          </Grid>
                          <Grid item xs={6}>
                            <Typography variant="caption" color="text.secondary">Better</Typography>
                            <Typography variant="body2" sx={{ minHeight: 40 }}>{ct.entries.better || '-'}</Typography>
                          </Grid>
                        </Grid>

                        {/* Section 3: Being a Hero */}
                        <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>
                          3. Being a Hero
                        </Typography>
                        <Grid container spacing={1} sx={{ mb: 2 }}>
                          <Grid item xs={6}>
                            <Typography variant="caption" color="text.secondary">New Commitment</Typography>
                            <Typography variant="body2" sx={{ minHeight: 40 }}>{ct.entries.newCommitment || '-'}</Typography>
                          </Grid>
                          <Grid item xs={6}>
                            <Typography variant="caption" color="text.secondary">New Courage</Typography>
                            <Typography variant="body2" sx={{ minHeight: 40 }}>{ct.entries.newCourage || '-'}</Typography>
                          </Grid>
                          <Grid item xs={6}>
                            <Typography variant="caption" color="text.secondary">New Capability</Typography>
                            <Typography variant="body2" sx={{ minHeight: 40 }}>{ct.entries.newCapability || '-'}</Typography>
                          </Grid>
                          <Grid item xs={6}>
                            <Typography variant="caption" color="text.secondary">New Confidence</Typography>
                            <Typography variant="body2" sx={{ minHeight: 40 }}>{ct.entries.newConfidence || '-'}</Typography>
                          </Grid>
                        </Grid>

                        {/* Section 4: Not Driving Others Crazy */}
                        <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>
                          4. Not Driving Others Crazy
                        </Typography>
                        <Grid container spacing={1}>
                          <Grid item xs={6}>
                            <Typography variant="caption" color="text.secondary">Show Up On Time</Typography>
                            <Typography variant="body2" sx={{ minHeight: 40 }}>{ct.entries.showUpOnTime || '-'}</Typography>
                          </Grid>
                          <Grid item xs={6}>
                            <Typography variant="caption" color="text.secondary">Do What You Say</Typography>
                            <Typography variant="body2" sx={{ minHeight: 40 }}>{ct.entries.doWhatYouSay || '-'}</Typography>
                          </Grid>
                          <Grid item xs={6}>
                            <Typography variant="caption" color="text.secondary">Finish What You Start</Typography>
                            <Typography variant="body2" sx={{ minHeight: 40 }}>{ct.entries.finishWhatYouStart || '-'}</Typography>
                          </Grid>
                          <Grid item xs={6}>
                            <Typography variant="caption" color="text.secondary">Say Please & Thank You</Typography>
                            <Typography variant="body2" sx={{ minHeight: 40 }}>{ct.entries.sayPleaseThankYou || '-'}</Typography>
                          </Grid>
                        </Grid>
                      </Paper>
                    </Grid>
                  ))}
                </Grid>
              ) : (
                <Paper sx={{ p: 4, textAlign: 'center' }}>
                  <Typography color="text.secondary">No Casting Tool submissions found</Typography>
                </Paper>
              )}
            </TabPanel>
          </>
        ) : (
          <Box sx={{ textAlign: 'center', py: 8 }}>
            <Typography color="text.secondary">Employee not found</Typography>
          </Box>
        )}
      </Box>
    </PageWithHeader>
  );
};

export default CEOEmployeeDetail;
